/* UnifiedWhiteboard — the single, state-of-the-art whiteboard for TeachingSoftware.ai.
 *
 * Replaces the old standalone `Whiteboard.jsx` (AI scene) AND the overlay
 * `CollabWhiteboard.jsx` (freehand collab). One canvas, one toolbar, everything.
 *
 * Modes
 *   • Solo: local scene, save/load via /api/whiteboard
 *   • Collab (toggle ON): freehand strokes + vector elements sync live via
 *     /api/live/{classId}/wb/{strokes,elements,cursors}. Both teacher AND
 *     students can add strokes/notes; teacher can clear.
 *
 * Tools
 *   Select · Pan · Pen · Highlighter · Eraser · Rectangle · Ellipse · Arrow
 *   Text · Sticky note · Math (KaTeX) · Image (upload / Openverse search)
 *   AI Diagram · Save · Load · Export PNG · Clear · Undo · Redo · Zoom · Reset view
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  MousePointer2, Hand, Pencil, Highlighter, Eraser, Square, Circle,
  ArrowRight, Type as TypeIcon, StickyNote, ImagePlus, Search,
  Sparkles, Wand2, Loader2, Save, FolderOpen, Trash2, Download, Plus,
  Undo2, Redo2, ZoomIn, ZoomOut, Maximize2, X, Users, Grid3x3, PenTool,
  ChevronDown, Palette, Ruler, Compass, Triangle, FileText as FileTextIcon, BookOpen, Calculator, LayoutGrid, Camera, Settings2,
} from "lucide-react";
import ImageSearchPalette from "@/components/ImageSearchPalette";
import WhiteboardCanvas from "@/components/whiteboard/WhiteboardCanvas";
import ScientificCalculator from "@/components/whiteboard/ScientificCalculator";
import WhiteboardAssetsPanel from "@/components/whiteboard/WhiteboardAssetsPanel";
import CurriculumSkillsPanel from "@/components/whiteboard/CurriculumSkillsPanel";
import { GeometryRuler, GeometryProtractor, GeometryCompass } from "@/components/whiteboard/GeometryTools";
import SubjectPicker from "@/components/whiteboard/SubjectPicker";
import SubjectFooter from "@/components/whiteboard/SubjectFooter";
import SnapshotModal from "@/components/whiteboard/SnapshotModal";
import CloudGalleryPicker from "@/components/whiteboard/CloudGalleryPicker";
import NeonGenOverlay from "@/components/whiteboard/NeonGenOverlay";
import SkillResponsesPanel from "@/components/live/SkillResponsesPanel";
import { pdfToElements } from "@/components/whiteboard/pdfImport";
import { useMobi } from "@/lib/MobiContext";
import {
  CANVAS_W, CANVAS_H, COLORS, STICKY_TINTS, STROKE_WIDTHS,
  rid, newRect, newEllipse, newText, newArrow, newImage, newSticky,
  mergeRemoteElement, elementToPayload,
} from "@/components/whiteboard/wbUtils";

const TOOLS = [
  { id: "text",       icon: TypeIcon,      title: "Text (supports $LaTeX$)", color: "#818CF8" }, // indigo
  { id: "select",     icon: MousePointer2, title: "Select (V)",        color: "#38BDF8" },  // sky
  { id: "pan",        icon: Hand,          title: "Pan (Space+drag)",  color: "#A78BFA" },  // violet
  { id: "pen",        icon: Pencil,        title: "Pen",               color: "#F472B6" },  // pink
  { id: "highlighter",icon: Highlighter,   title: "Highlighter",       color: "#FBBF24" },  // amber
  { id: "eraser",     icon: Eraser,        title: "Eraser",            color: "#F87171" },  // red
  { id: "rectangle",  icon: Square,        title: "Rectangle",         color: "#34D399" },  // emerald
  { id: "ellipse",    icon: Circle,        title: "Ellipse",           color: "#22D3EE" },  // cyan
  { id: "arrow",      icon: ArrowRight,    title: "Arrow",             color: "#FB923C" },  // orange
  { id: "sticky",     icon: StickyNote,    title: "Sticky note",       color: "#FDE68A" },  // yellow
];

const AI_STYLES = ["diagram", "flow", "mindmap", "timeline"];

export default function UnifiedWhiteboard({
  embedded = false,
  classId: propClassId,
  subjectId: propSubjectId,
  isTeacher: propIsTeacher,
  defaultCollab = false,
  forcedTheme = null,     // students in class follow the teacher's board theme
  onThemeChange,          // teacher broadcasts theme flips to the class
  readOnly = false,       // student without draw permission — view only
  collabPermission,       // { enabled, allowed_ids, allowed_names } — display state for the teacher toggle
  onCollabControl,        // teacher-in-class: open the "who can draw" modal instead of local toggle
  followView = null,      // mirrored viewport from the class state (screen-mirror)
  onViewBroadcast,        // controller pushes their viewport for everyone to follow
  guest = null,           // { token, name } — invite-link guest (unauthenticated) mode
  onClose,
} = {}) {
  const [params] = useSearchParams();
  const linkedClassId = propClassId ?? params.get("classId") ?? "";
  const subjectId = propSubjectId ?? params.get("subjectId") ?? "";
  const isTeacher = propIsTeacher !== undefined ? propIsTeacher : true; // standalone route → teacher

  // Guest mode adapter — invite-link students aren't authenticated, so board
  // sync routes through the token-validated /public/class/{id}/wb-* endpoints
  // instead of the JWT-protected /live/{id}/wb/* ones.
  const wbGet = useCallback((name, reqParams) => guest
    ? api.get(`/public/class/${linkedClassId}/wb-${name}`, { params: { ...(reqParams || {}), token: guest.token } })
    : api.get(`/live/${linkedClassId}/wb/${name}`, { params: reqParams }), [guest, linkedClassId]);
  const wbPost = useCallback((name, body) => guest
    ? api.post(`/public/class/${linkedClassId}/wb-${name.replace(/\//g, "-")}`, { ...(body || {}), token: guest.token, guest_name: guest.name })
    : api.post(`/live/${linkedClassId}/wb/${name}`, body), [guest, linkedClassId]);

  // Live skill-widget context — lets MCQ widgets on the board report each
  // student's picks to the teacher's Live Responses panel.
  useEffect(() => {
    if (!embedded || !linkedClassId) return;
    window.__liveSkillCtx = { classId: linkedClassId, role: isTeacher ? "teacher" : "student", guest: guest || null };
    return () => {
      if (window.__liveSkillCtx?.classId === linkedClassId) window.__liveSkillCtx = null;
    };
  }, [embedded, linkedClassId, isTeacher, guest]);

  // Scene state
  const [elements, setElements] = useState([]);
  const [strokes, setStrokes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("Untitled board");
  const [currentSceneId, setCurrentSceneId] = useState("");
  const [scenes, setScenes] = useState([]);
  const [savedOpen, setSavedOpen] = useState(false);

  // Tool state
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState(() => {
    // Dark board default pen = blue (ink-black blends into the dark canvas)
    try { return (localStorage.getItem("wb.theme") || "dark") === "dark" ? "#0EA5E9" : COLORS[0]; }
    catch { return "#0EA5E9"; }
  });
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [stickyTint, setStickyTint] = useState(STICKY_TINTS[0]);
  const [gridOn, setGridOn] = useState(true);
  const [showCalc, setShowCalc] = useState(false);
  const [showAssetsPanel, setShowAssetsPanel] = useState(false); // legacy — kept only so addAssetToBoard's toast helpers stay compiled; the right-side panel is retired
  const [showSkillsPanel, setShowSkillsPanel] = useState(false);
  const [mobileToolSheetOpen, setMobileToolSheetOpen] = useState(false); // mobile bottom sheet with the tool rail
  const [mobileBarOpen, setMobileBarOpen] = useState(false); // mobile: collapse the whole top bar behind one "Controls" button

  // Viewport
  const [view, setView] = useState({ panX: 0, panY: 0, zoom: 1 });

  // Collab
  // Students embedded from a live class always draw in collab mode — the whole
  // point is 2-way sync with the teacher (fixes Ninis #13). Teachers can toggle.
  const [collab, setCollab] = useState((defaultCollab || !propIsTeacher) && !!linkedClassId);
  const [peerCursors, setPeerCursors] = useState([]);
  const strokesSinceRef = useRef("");
  const elementsSinceRef = useRef("");
  const cursorPushRef = useRef(0);
  const localElementIdsRef = useRef(new Set());   // ids we've pushed / edited locally so we don't overwrite our own state on merge

  // AI panel — kept for legacy code paths, but the UI is now a modal
  const [aiPrompt, setAIPrompt] = useState("");
  const [aiMode, setAIMode] = useState("assessment"); // assessment (photo) | visualization (3D transparent)
  const [snapshotOpen, setSnapshotOpen] = useState(false);

  // Students: poll for the teacher's snapshot request and auto-open the camera.
  const snapReqRef = useRef("");
  useEffect(() => {
    if (!embedded || isTeacher || !linkedClassId || guest) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/live/${linkedClassId}/snapshot-request`);
        const at = data?.at || "";
        if (!at || at === snapReqRef.current) return;
        const fresh = Date.now() - new Date(at).getTime() < 60000;
        snapReqRef.current = at;
        if (fresh) {
          toast.info("Your teacher requests a snapshot — show your work to the camera");
          setSnapshotOpen(true);
        }
      } catch { /* not in class yet */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [embedded, isTeacher, linkedClassId, guest]);
  const [snapshotReview, setSnapshotReview] = useState(null); // teacher reviewing a student snapshot
  const [aiStyle, setAIStyle] = useState("diagram");
  const [aiLoading, setAILoading] = useState(false);
  const [imgPalette, setImgPalette] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const fileRef = useRef(null);
  const pdfFileRef = useRef(null);
  const docFileRef = useRef(null);
  const [insertOpen, setInsertOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [genFx, setGenFx] = useState(null); // futuristic full-screen "AI generating" overlay label
  const [insertUrlOpen, setInsertUrlOpen] = useState(false);
  const [insertUrlValue, setInsertUrlValue] = useState("");
  const [pdfBusy, setPdfBusy] = useState({ on: false, page: 0, total: 0 });
  // PDF page-nav — list of {id, y, title, thumbSrc?} entries, populated after import
  const [pdfPages, setPdfPages] = useState([]); // {id, y, title, thumb}

  // Whiteboard theme — light or dark. Persisted in localStorage.
  // Default is DARK — the Interactive Board is meant to feel like a modern
  // dark-mode canvas out of the box.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("wb.theme") || "dark"; } catch { return "dark"; }
  });
  // Students inside a live class follow the teacher's theme (fixes the
  // "teacher draws on dark board, student sees blank light board" bug).
  const effTheme = forcedTheme || theme;
  const isDark = effTheme === "dark";
  useEffect(() => {
    try { localStorage.setItem("wb.theme", theme); } catch { /* ignore */ }
  }, [theme]);
  // Semantic tokens the rest of the whiteboard consumes
  const tk = isDark
    ? { bg: "#0A0C10", surface: "#141821", border: "rgba(255,255,255,0.08)", text: "#E5E7EB", subtext: "#9CA3AF", canvasBg: "#0F1218", railBg: "#141821", railText: "#E5E7EB", railHover: "rgba(255,255,255,0.06)" }
    : { bg: "#FBFAF7", surface: "#FFFFFF", border: "rgba(0,0,0,0.10)", text: "#0A0C10", subtext: "#6B7280", canvasBg: "#F5F5F5", railBg: "#FFFFFF", railText: "#404040", railHover: "rgba(0,0,0,0.05)" };

  // Geometry tools (world coords)
  const [ruler, setRuler]           = useState(null); // {x,y,rotation}
  const [protractor, setProtractor] = useState(null); // {x,y,rotation}
  const [compass, setCompass]       = useState(null); // {x,y,r,a}
  const canvasContainerRef = useRef(null);
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);

  // Subject picker — pulled from teacher's real subjects list
  const [selectedSubject, setSelectedSubject] = useState(null); // full subject object

  // Publish Mobi context so the global advisor knows what the user is doing
  const { setMobiContext } = useMobi();
  useEffect(() => {
    setMobiContext({
      page: "whiteboard",
      title: selectedSubject ? `Interactive Board · ${selectedSubject.name}` : "Interactive Board",
      hint: `The teacher is on the Unified Whiteboard. Selected tool = "${tool}". Elements on canvas: ${elements.length}. ${collab ? "Collab mode is ON — students see the same board live." : "Solo mode."} ${selectedSubject ? `Subject kit "${selectedSubject.name}" is active with props like scale, beaker, circuit-bulb, etc.` : "No subject kit selected yet."}`,
      data: {
        subject: selectedSubject?.name || "",
        collab: collab ? "on" : "off",
        elementCount: elements.length,
      },
      suggestions: selectedSubject
        ? [`Suggest a 5-min activity for ${selectedSubject.name}`, "Draw a diagram of…", "Which prop should I use next?"]
        : ["Which subject should I teach next?", "How do I add a subject?", "Suggest a whiteboard activity"],
    });
    return () => setMobiContext(null);
  }, [selectedSubject, tool, elements.length, collab, setMobiContext]);

  // History for undo/redo (element + stroke arrays)
  const historyRef = useRef({ past: [], future: [] });

  const pushHistory = useCallback(() => {
    historyRef.current.past.push({ elements: [...elements], strokes: [...strokes] });
    if (historyRef.current.past.length > 40) historyRef.current.past.shift();
    historyRef.current.future = [];
  }, [elements, strokes]);

  const undo = useCallback(() => {
    const past = historyRef.current.past;
    if (!past.length) return;
    const prev = past.pop();
    historyRef.current.future.push({ elements: [...elements], strokes: [...strokes] });
    setElements(prev.elements);
    setStrokes(prev.strokes);
  }, [elements, strokes]);

  const redo = useCallback(() => {
    const future = historyRef.current.future;
    if (!future.length) return;
    const nxt = future.pop();
    historyRef.current.past.push({ elements: [...elements], strokes: [...strokes] });
    setElements(nxt.elements);
    setStrokes(nxt.strokes);
  }, [elements, strokes]);

  // ── Load saved scenes (all of the teacher's boards — subject filtering
  // hid boards saved elsewhere and confused users) ──
  useEffect(() => {
    if (guest) return; // guests have no saved scenes
    (async () => {
      try {
        const { data } = await api.get("/whiteboard");
        setScenes(data.scenes || []);
      } catch { /* silent */ }
    })();
  }, [subjectId, guest]);

  // ── Collab polling: strokes + elements + cursors ──
  useEffect(() => {
    if (!collab || !linkedClassId) return;
    let stop = false;
    const tick = async () => {
      try {
        const [stR, elR, curR] = await Promise.all([
          wbGet("strokes", strokesSinceRef.current ? { since: strokesSinceRef.current } : {}),
          wbGet("elements", elementsSinceRef.current ? { since: elementsSinceRef.current } : {}),
          wbGet("cursors"),
        ]);
        if (stop) return;
        const freshStrokes = stR.data?.strokes || [];
        if (freshStrokes.length) {
          strokesSinceRef.current = freshStrokes[freshStrokes.length - 1].created_at;
          setStrokes((prev) => {
            let next = prev;
            for (const s of freshStrokes) {
              if (s.tool === "clear") next = [];
              else if (s.tool === "erase") next = next.filter((p) => !(s.erased_ids || []).includes(p.id));
              else if (!next.some((p) => p.id === s.id)) next = [...next, s];
            }
            return next;
          });
        }
        const freshEls = elR.data?.elements || [];
        if (freshEls.length) {
          elementsSinceRef.current = freshEls[freshEls.length - 1].updated_at;
          setElements((prev) => {
            let next = prev;
            for (const el of freshEls) {
              // Skip echo of our own local element unless it was really updated remotely
              if (localElementIdsRef.current.has(el.id) && !el.deleted) {
                localElementIdsRef.current.delete(el.id);
                continue;
              }
              next = mergeRemoteElement(next, el);
            }
            return next;
          });
        }
        setPeerCursors(curR.data?.cursors || []);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 800);
    return () => { stop = true; clearInterval(t); };
  }, [collab, linkedClassId, wbGet]);

  // ── Seed-sync: board content that exists BEFORE collab starts (loaded
  // saves, pre-class drawings) must reach students too — push anything the
  // server doesn't have yet. ──
  const seedSync = useCallback(async (els, strs) => {
    if (!collab || !linkedClassId || readOnly) return;
    try {
      const [stR, elR] = await Promise.all([
        wbGet("strokes"),
        wbGet("elements"),
      ]);
      const serverStrokeIds = new Set((stR.data?.strokes || []).map((s) => s.id));
      const serverElIds = new Set((elR.data?.elements || []).map((e) => e.id));
      for (const el of els) {
        if (serverElIds.has(el.id)) continue;
        try { await api.post(`/live/${linkedClassId}/wb/element`, elementToPayload(el)); } catch { /* skip */ }
      }
      for (const s of strs) {
        if (serverStrokeIds.has(s.id) || s.tool === "erase" || s.tool === "clear" || !s.points?.length) continue;
        try {
          const { data } = await wbPost("stroke", {
            points: s.points, color: s.color, width: s.width, tool: s.tool,
          });
          const sid = data?.stroke?.id;
          if (sid) setStrokes((cur) => cur.map((x) => (x.id === s.id ? { ...x, id: sid } : x)));
        } catch { /* skip */ }
      }
    } catch { /* offline — polling will retry naturally on next open */ }
  }, [collab, linkedClassId, readOnly, wbGet, wbPost]);

  const seededForRef = useRef("");
  useEffect(() => {
    if (!collab || !linkedClassId || readOnly) return;
    if (seededForRef.current === linkedClassId) return;
    seededForRef.current = linkedClassId;
    const t = setTimeout(() => seedSync(elements, strokes), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collab, linkedClassId, readOnly]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e) => {
      if (editing) return;
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault(); deleteSelected(); return;
      }
      const map = { v: "select", h: "pan", p: "pen", e: "eraser", r: "rectangle", o: "ellipse", a: "arrow", t: "text", s: "sticky" };
      if (map[e.key?.toLowerCase()]) setTool(map[e.key.toLowerCase()]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, selectedId, undo, redo]);

  // ── Element sync helpers ──
  const pushElement = useCallback(async (el) => {
    if (!collab || !linkedClassId) return;
    localElementIdsRef.current.add(el.id);
    try {
      let payload = elementToPayload(el);
      // Data-URL images (file import / PDF pages / AI images) are megabytes —
      // store them server-side and sync a light URL so students actually see them.
      if (payload.type === "image" && (payload.src || "").startsWith("data:") && !guest) {
        try {
          const { data } = await api.post(`/live/${linkedClassId}/wb/upload-image`, { data_url: payload.src });
          if (data?.url) {
            payload = { ...payload, src: data.url };
            setElements((els) => els.map((e) => (e.id === el.id ? { ...e, src: data.url } : e)));
          }
        } catch { /* fall through — push as-is */ }
      }
      await wbPost("element", payload);
    } catch { /* ignore */ }
  }, [collab, linkedClassId, guest, wbPost]);

  const pushDeleteElement = useCallback(async (id) => {
    if (!collab || !linkedClassId) return;
    try {
      await wbPost("element/delete", { id });
    } catch { /* ignore */ }
  }, [collab, linkedClassId, wbPost]);

  // ── Element operations ──
  const addElement = useCallback((el) => {
    pushHistory();
    setElements((els) => [...els, el]);
    setSelectedId(el.id);
    pushElement(el);
  }, [pushHistory, pushElement]);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    pushHistory();
    setElements((els) => els.filter((e) => e.id !== selectedId));
    pushDeleteElement(selectedId);
    setSelectedId("");
  }, [selectedId, pushHistory, pushDeleteElement]);

  // Canvas-driven element creation (tools like text/sticky/math or shape-draft)
  const onElementCreate = useCallback(({ tool: tid, x, y, element }) => {
    if (tid === "shape-direct" && element) {
      addElement({ ...element, color: element.color || color });
      setTool("select");
      return;
    }
    if (tid === "text") {
      // Drop a text box and start typing immediately — no "Heading" placeholder.
      // Open the editor only after the placing click fully completes, otherwise
      // the click's own mouseup blurs the just-mounted textarea and closes it.
      const el = newText(x, y, color);
      addElement(el);
      const openEditor = () => setTimeout(() => setEditing({ id: el.id, kind: "text" }), 30);
      window.addEventListener("mouseup", openEditor, { once: true });
      window.addEventListener("touchend", openEditor, { once: true });
      setTool("select");
      return;
    }
    if (tid === "sticky") { addElement(newSticky(x, y, stickyTint)); setTool("select"); return; }
  }, [addElement, color, stickyTint]);

  // Element committed (drag end or label edit)
  const onElementCommit = useCallback((el) => { pushElement(el); }, [pushElement]);

  // Stroke committed (freehand)
  const onStrokeEnd = useCallback(async (stroke) => {
    pushHistory();
    const local = {
      ...stroke,
      id: `local-${Date.now()}-${rid()}`,
      user_role: "local",
      created_at: new Date().toISOString(),
    };
    setStrokes((s) => [...s, local]);
    if (collab && linkedClassId) {
      try {
        const { data } = await wbPost("stroke", stroke);
        // Adopt the server id so erases stay in sync across all viewers.
        const sid = data?.stroke?.id;
        if (sid) setStrokes((s) => s.map((x) => (x.id === local.id ? { ...x, id: sid } : x)));
      }
      catch (e) { toast.error(e.response?.data?.detail || "Could not sync stroke"); }
    }
  }, [collab, linkedClassId, pushHistory, wbPost]);

  // Eraser — whole strokes touched by the pointer are removed + synced.
  const eraseStrokes = useCallback((ids) => {
    if (!ids?.length) return;
    pushHistory();
    setStrokes((s) => s.filter((x) => !ids.includes(x.id)));
    if (collab && linkedClassId) {
      const serverIds = ids.filter((i) => !String(i).startsWith("local-"));
      if (serverIds.length) {
        wbPost("stroke/erase", { ids: serverIds }).catch(() => {});
      }
    }
  }, [collab, linkedClassId, pushHistory, wbPost]);

  // Peer cursor push (throttled)
  const onCursorMove = useCallback((p) => {
    if (!collab || !linkedClassId) return;
    const now = Date.now();
    if (now - cursorPushRef.current < 350) return;
    cursorPushRef.current = now;
    wbPost("cursor", {
      x: +(p.x / CANVAS_W).toFixed(4),
      y: +(p.y / CANVAS_H).toFixed(4),
    }).catch(() => {});
  }, [collab, linkedClassId, wbPost]);

  // ── Toolbar actions ──
  const clearAll = useCallback(async () => {
    if (!window.confirm(collab ? "Wipe the whiteboard for everyone in the class?" : "Clear the whole board?")) return;
    pushHistory();
    setElements([]);
    setStrokes([]);
    setSelectedId("");
    if (collab && linkedClassId && isTeacher) {
      try {
        await Promise.all([
          api.post(`/live/${linkedClassId}/wb/clear`),
          api.post(`/live/${linkedClassId}/wb/elements/clear`),
        ]);
        toast.success("Whiteboard cleared for everyone");
      } catch (e) { toast.error(e.response?.data?.detail || "Clear failed"); }
    }
  }, [collab, linkedClassId, isTeacher, pushHistory]);

  const resetView = () => setView({ panX: 0, panY: 0, zoom: 1 });
  const zoomIn = () => setView((v) => ({ ...v, zoom: Math.min(3, v.zoom * 1.2) }));
  const zoomOut = () => setView((v) => ({ ...v, zoom: Math.max(0.25, v.zoom / 1.2) }));

  // File import
  const onFileImport = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Image files only");
    if (f.size > 5 * 1024 * 1024) return toast.error("Please pick an image under 5 MB");
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const probe = new Image();
      probe.onload = () => {
        const ratio = probe.height / probe.width || 0.75;
        const w = 360;
        const h = Math.round(w * ratio);
        addElement(newImage(dataUrl, 320, 260, w, Math.min(h, 500), f.name));
      };
      probe.onerror = () => addElement(newImage(dataUrl, 320, 260, 320, 240, f.name));
      probe.src = dataUrl;
    };
    reader.readAsDataURL(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  // PDF import — render each page → data URL → insert as vertical image stack
  const onPdfImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please pick a PDF file");
      return;
    }
    if (f.size > 25 * 1024 * 1024) return toast.error("PDF must be under 25 MB");
    setPdfBusy({ on: true, page: 0, total: 0 });
    try {
      const startX = view.panX + 60;
      const startY = view.panY + 60;
      const pages = await pdfToElements(f, {
        pageWidth: 800,
        startX, startY,
        onProgress: (i, total) => setPdfBusy({ on: true, page: i, total }),
      });
      pushHistory();
      setElements((els) => [...els, ...pages]);
      setPdfPages(pages.map((p, i) => ({ id: p.id, y: p.y, x: p.x, title: `Page ${i + 1}`, thumb: p.src })));
      if (collab && linkedClassId) pages.forEach((el) => pushElement(el));
      toast.success(`Imported ${pages.length} page${pages.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Could not read PDF — try a smaller/simpler file");
    } finally {
      setPdfBusy({ on: false, page: 0, total: 0 });
      if (pdfFileRef.current) pdfFileRef.current.value = "";
    }
  };

  // Subject-footer prop insertion — insert an interactive prop element at view center
  const insertProp = (tool) => {
    const x = view.panX + (CANVAS_W / view.zoom) / 2 - 120;
    const y = view.panY + (CANVAS_H / view.zoom) / 2 - 120;
    const newEls = tool.build(x, y).map((el) => ({ ...el, id: rid() }));
    pushHistory();
    setElements((els) => [...els, ...newEls]);
    setSelectedId(newEls[0]?.id || "");
    setTool("select"); // so teacher can immediately drag / resize the prop
    if (collab && linkedClassId) newEls.forEach((el) => pushElement(el));
  };

  // Geometry-tool spawners — place near current view center
  const spawnGeoRuler = () => {
    if (ruler) { setRuler(null); return; }
    setRuler({ x: view.panX + (CANVAS_W / view.zoom) / 2 - 300, y: view.panY + (CANVAS_H / view.zoom) / 2 - 30, rotation: 0 });
  };
  const spawnGeoProtractor = () => {
    if (protractor) { setProtractor(null); return; }
    setProtractor({ x: view.panX + (CANVAS_W / view.zoom) / 2, y: view.panY + (CANVAS_H / view.zoom) / 2 + 120, rotation: 0 });
  };
  const spawnGeoCompass = () => {
    if (compass) { setCompass(null); return; }
    setCompass({ x: view.panX + (CANVAS_W / view.zoom) / 2, y: view.panY + (CANVAS_H / view.zoom) / 2, r: 140, a: 0 });
  };

  // Compass "draw" commits its arc as a freehand stroke
  const onCompassDrawArc = useCallback(async (arcPts) => {
    if (!arcPts || arcPts.length < 2) return;
    const stroke = {
      color, width: strokeWidth, tool: "pen",
      points: arcPts.map((p) => ({ x: +(p.x / CANVAS_W).toFixed(4), y: +(p.y / CANVAS_H).toFixed(4) })),
    };
    pushHistory();
    const local = { ...stroke, id: `local-${Date.now()}-${rid()}`, user_role: "local", created_at: new Date().toISOString() };
    setStrokes((s) => [...s, local]);
    if (collab && linkedClassId) {
      try { await wbPost("stroke", stroke); } catch { /* ignore */ }
    }
  }, [color, strokeWidth, collab, linkedClassId, pushHistory, wbPost]);

  const addImageFromSearch = (r) => {
    const ratio = (r.height && r.width) ? r.height / r.width : 0.66;
    const w = 320;
    const h = Math.round(w * ratio);
    addElement(newImage(r.url, 320, 260, w, Math.min(h, 500), r.title || ""));
  };

  // Place an image picked from the Cloud Gallery (upload or reuse) on the board.
  const placeGalleryImage = (url, title) => {
    const probe = new Image();
    probe.onload = () => {
      const ratio = probe.height / probe.width || 0.75;
      const w = 360;
      addElement(newImage(url, 320, 260, w, Math.min(Math.round(w * ratio), 500), title || ""));
    };
    probe.onerror = () => addElement(newImage(url, 320, 260, 320, 240, title || ""));
    probe.src = url;
    setGalleryOpen(false);
    toast.success("Added to the board");
  };

  // Drop an asset from the Assets Panel onto the whiteboard. Behaviour depends
  // on the asset's kind:
  //   • image/diagram → inline image element
  //   • skill         → interactive MCQ practice widget on the canvas
  //   • other         → sticky-note fallback
  const addAssetToBoard = async (a) => {
    if (!a) return;
    const px = 340;
    const py = 260;
    if (a.url && (a.kind === "image" || a.kind === "diagram")) {
      addElement(newImage(a.url, px, py, 340, 240, a.title || ""));
      toast.success(`Added · ${a.title}`);
      return;
    }
    if (a.kind === "skill") {
      // Pull the full skill so we have the questions payload.
      try {
        const { data } = await api.get(`/skills/${a.id}`);
        const skill = data.skill || {};
        const questions = skill.payload?.questions || [];
        if (!questions.length) {
          toast.error("This skill has no questions yet");
          return;
        }
        addElement({
          id: rid(),
          type: "prop",
          propType: "skill-widget",
          x: px, y: py, width: 520, height: 380,
          color: "#7C3AED",
          label: "",
          state: {
            skillId: skill.id,
            title: skill.title || a.title || "Skill",
            topic: skill.topic || "",
            difficulty: skill.difficulty || "",
            questions,
            idx: 0,
            answers: [],
            picked: null,
            revealed: false,
            done: false,
            startedAt: Date.now(),
          },
        });
        toast.success(`Skill added · ${skill.title}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load skill");
      }
      return;
    }
    // Fallback — drop a sticky-note style card that references the asset.
    const label = `${(a.kind || "asset").toUpperCase()}\n${a.title || "Untitled"}${a.count ? `\n${a.count} items` : ""}`;
    addElement({
      id: rid(),
      type: "sticky",
      x: px, y: py, width: 220, height: 140,
      text: label, color: "#FEF3C7", fontSize: 14,
    });
    toast.success(`Added · ${a.title}`);
  };

  // Curriculum practice: when the left Skills panel yields a full skill doc
  // (already generated with questions), drop the interactive widget onto the
  // board directly. Reused by CurriculumSkillsPanel.
  const dropSkillWidget = (skill, meta = {}) => {
    if (!skill) return;
    const questions = skill.payload?.questions || [];
    if (!questions.length) {
      toast.error("This skill has no questions");
      return;
    }
    addElement({
      id: rid(),
      type: "prop",
      propType: "skill-widget",
      x: 340, y: 260, width: 520, height: 380,
      color: "#7C3AED",
      label: "",
      state: {
        skillId: skill.id,
        title: skill.title || "Skill",
        topic: skill.topic || meta.topic || "",
        difficulty: skill.difficulty || "",
        // Curriculum coordinates — enable the in-widget ↻ regenerate button.
        subject: meta.subject || "",
        grade: meta.grade || "",
        questions,
        idx: 0,
        answers: [],
        picked: null,
        revealed: false,
        done: false,
        startedAt: Date.now(),
      },
    });
  };

  // Curriculum topic → transparent AI visual placed on the board (beside the
  // activity widget when both are generated together).
  const dropTopicVisual = async (topic, meta = {}, beside = false) => {
    setGenFx("Synthesizing visual");
    try {
      const { data } = await api.post("/ai/image", {
        prompt: `${topic} — ${meta.subject || ""} ${meta.grade || ""} educational illustration`.trim(),
        kind: "visual3d",
        size: "1024x1024",
        // Green-ticked topics reuse the cached asset — no regeneration, no credits.
        cache_key: (meta.subject && meta.grade) ? `visual:${meta.subject}:${meta.grade}:${topic.toLowerCase()}` : "",
      });
      const url = data?.result?.url || data?.content?.url;
      const b64 = data?.result?.b64_json || data?.content?.data;
      const src = url || (b64 ? `data:image/png;base64,${b64}` : "");
      if (!src) { setGenFx(null); toast.error("Visual generation returned no image", { id: "topic-visual" }); return; }
      addElement(newImage(src, beside ? 900 : 360, 260, 380, 380, topic));
      toast.success("Visual added to the board", { id: "topic-visual" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate visual", { id: "topic-visual" });
    } finally { setGenFx(null); }
  };

  // AI diagram gen
  const generateAI = async () => {
    if (!aiPrompt.trim()) return toast.error("Type a prompt to generate an image");
    setAILoading(true);
    try {
      // Use the raster image endpoint (Nano Banana / OpenAI Image) — NOT the
      // diagram/SVG generator. This produces a proper high-quality picture
      // matching the prompt, then places it on the board as an image element.
      const { data } = await api.post("/ai/image", {
        prompt: aiPrompt.trim(),
        kind: aiMode === "visualization" ? "visual3d" : "photo",
        size: "1024x1024",
      });
      const url = data?.result?.url || data?.content?.url;
      const b64 = data?.result?.b64_json || data?.content?.data;
      const src = url || (b64 ? `data:image/png;base64,${b64}` : "");
      if (!src) {
        toast.error("Image generation returned no image");
        return;
      }
      pushHistory();
      const w = 420, h = 420;
      const cx = -view.panX + (window.innerWidth / 2) / view.zoom - w / 2;
      const cy = -view.panY + (window.innerHeight / 2) / view.zoom - h / 2;
      const el = newImage(src, cx, cy, w, h, aiPrompt.slice(0, 60));
      addElement(el);
      toast.success("Image added to board");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally { setAILoading(false); }
  };

  // Persistence
  const saveBoard = async () => {
    try {
      const { data } = await api.post("/whiteboard/save", {
        id: currentSceneId || undefined,
        title: title.trim() || "Untitled board",
        class_id: linkedClassId,
        subject_id: subjectId,
        elements,
        app_state: { view, strokes },
      });
      setCurrentSceneId(data.id);
      toast.success(`Saved · ${title}`);
      // refresh list
      const url = subjectId ? `/whiteboard?subject_id=${subjectId}` : "/whiteboard";
      const list = await api.get(url); setScenes(list.data.scenes || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const openScene = async (id) => {
    try {
      const { data } = await api.get(`/whiteboard/${id}`);
      const s = data.scene;
      setCurrentSceneId(s.id);
      setTitle(s.title || "Untitled board");
      setElements(s.elements || []);
      setStrokes((s.app_state && s.app_state.strokes) || []);
      if (s.app_state?.view) setView(s.app_state.view);
      // A loaded board must reach students when we're live
      seedSync(s.elements || [], (s.app_state && s.app_state.strokes) || []);
      toast.success(`Loaded · ${s.title}`);
      setSavedOpen(false);
    } catch { toast.error("Open failed"); }
  };

  const deleteScene = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this board permanently?")) return;
    try {
      await api.delete(`/whiteboard/${id}`);
      if (id === currentSceneId) { setCurrentSceneId(""); setElements([]); setStrokes([]); }
      const url = subjectId ? `/whiteboard?subject_id=${subjectId}` : "/whiteboard";
      const list = await api.get(url); setScenes(list.data.scenes || []);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const newBoard = () => {
    pushHistory();
    setElements([]);
    setStrokes([]);
    setSelectedId("");
    setCurrentSceneId("");
    setTitle("Untitled board");
    setView({ panX: 0, panY: 0, zoom: 1 });
    toast.success("New blank board");
  };

  // Export PNG via SVG serialization — inlines external images as data URIs to avoid canvas taint.
  const exportPNG = async () => {
    const svgEl = document.querySelector('[data-testid="wb-svg"]');
    if (!svgEl) return;
    const svgClone = svgEl.cloneNode(true);
    svgClone.setAttribute("viewBox", `0 0 ${CANVAS_W} ${CANVAS_H}`);
    svgClone.setAttribute("width", CANVAS_W);
    svgClone.setAttribute("height", CANVAS_H);

    // Inline external <image> hrefs as data URIs so the raster canvas doesn't get tainted
    const imgNodes = Array.from(svgClone.querySelectorAll("image"));
    await Promise.all(imgNodes.map(async (node) => {
      const href = node.getAttribute("href") || node.getAttribute("xlink:href") || "";
      if (!href || href.startsWith("data:")) return;
      try {
        const resp = await fetch(href, { mode: "cors" });
        if (!resp.ok) throw new Error("fetch failed");
        const blob = await resp.blob();
        const dataUrl = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(String(fr.result || ""));
          fr.onerror = rej;
          fr.readAsDataURL(blob);
        });
        node.setAttribute("href", dataUrl);
        node.removeAttribute("xlink:href");
      } catch {
        // Strip the untrustable image so at least the rest of the board exports
        node.parentNode?.removeChild(node);
      }
    }));

    const svgData = new XMLSerializer().serializeToString(svgClone);
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = CANVAS_W; c.height = CANVAS_H;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#FBFAF7"; ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0);
        c.toBlob((b) => {
          if (!b) { toast.error("Export failed"); URL.revokeObjectURL(url); return; }
          const a = document.createElement("a");
          a.href = URL.createObjectURL(b);
          a.download = `${title.replace(/\s+/g, "_") || "whiteboard"}.png`;
          a.click();
          URL.revokeObjectURL(a.href);
          URL.revokeObjectURL(url);
        }, "image/png");
      } catch (err) {
        toast.error("Export blocked by image CORS. Try boards without remote images.");
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => { toast.error("Export failed"); URL.revokeObjectURL(url); };
    img.src = url;
  };

  const onCollabToggle = () => {
    // Inside a live class the teacher's toggle opens the "who can draw"
    // permission modal instead of a local flip (collab is off by default;
    // teacher decides all vs specific students, changeable anytime).
    if (embedded && isTeacher && onCollabControl) {
      onCollabControl();
      return;
    }
    if (!linkedClassId) {
      toast.error("Open the whiteboard from inside a class to enable collab.");
      return;
    }
    if (!isTeacher) {
      toast.info(readOnly ? "The teacher hasn't given you draw access yet." : "You can draw — the teacher sees your strokes live.");
      return;
    }
    setCollab((v) => !v);
    toast.success(!collab ? "Collab mode ON · 2-way sync live" : "Collab mode OFF · solo edits");
  };
  // Which state drives the toggle's visual — the class-level permission when
  // provided (live class), else the local collab flag.
  const collabOn = collabPermission ? !!collabPermission.enabled : collab;

  // View-only students park on the pan tool.
  useEffect(() => { if (readOnly) setTool("pan"); }, [readOnly]);

  // ── Screen-mirror: broadcast MY viewport (throttled) when I'm a controller,
  //    adopt the incoming shared viewport otherwise. ──
  const lastLocalViewMoveRef = useRef(0);
  const adoptingViewRef = useRef(false);
  const viewPushTimerRef = useRef(null);
  const lastAppliedViewRef = useRef("");
  useEffect(() => {
    if (!embedded || !onViewBroadcast) return;
    if (adoptingViewRef.current) { adoptingViewRef.current = false; return; }
    lastLocalViewMoveRef.current = Date.now();
    clearTimeout(viewPushTimerRef.current);
    viewPushTimerRef.current = setTimeout(() => {
      onViewBroadcast({ panX: view.panX, panY: view.panY, zoom: view.zoom });
    }, 400);
    return () => clearTimeout(viewPushTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, embedded]);
  useEffect(() => {
    if (!embedded || !followView || !followView.updated_at) return;
    if (followView.updated_at === lastAppliedViewRef.current) return;
    // Controllers ignore echoes of their own recent movement
    if (onViewBroadcast && Date.now() - lastLocalViewMoveRef.current < 2500) return;
    lastAppliedViewRef.current = followView.updated_at;
    adoptingViewRef.current = true;
    setView({ panX: followView.panX || 0, panY: followView.panY || 0, zoom: followView.zoom || 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followView, embedded]);

  // Teacher: watch for incoming student AI Snapshots → toast + review modal
  const seenSnapsRef = useRef(null);
  useEffect(() => {
    if (!embedded || !isTeacher || !linkedClassId) return;
    let stop = false;
    const startAt = new Date().toISOString();
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${linkedClassId}/snapshots`, { params: { since: startAt } });
        if (stop) return;
        if (!seenSnapsRef.current) seenSnapsRef.current = new Set();
        for (const s of data.snapshots || []) {
          if (s.by_role === "teacher" || seenSnapsRef.current.has(s.id)) continue;
          seenSnapsRef.current.add(s.id);
          toast.info(`${s.by_name} shared an AI Snapshot`, {
            duration: 12000,
            action: { label: "Review", onClick: () => setSnapshotReview(s) },
          });
        }
      } catch { /* keep polling */ }
    };
    const t = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [embedded, isTeacher, linkedClassId]);

  // Convenience for the mobile FAB — shows the current tool's colour + icon
  const activeToolMeta = TOOLS.find((t) => t.id === tool) || TOOLS[0];

  return (
    <div className={embedded ? "h-full w-full flex flex-col overflow-hidden" : "min-h-[calc(100vh-64px)] w-full flex flex-col overflow-y-auto"}
         style={{ background: tk.bg, color: tk.text }}
         data-testid="unified-whiteboard" data-theme={effTheme}>
      {/* ─── Top bar ─── */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 border-b"
           style={{ background: tk.surface, color: tk.text, borderColor: tk.border }}>
        {/* Mobile/tablet compact row — one "Controls" button reveals the full
            toolbar so the canvas keeps almost the whole screen. */}
        <div className="flex lg:hidden items-center gap-2 w-full min-w-0">
          <button
            onClick={() => setMobileBarOpen((v) => !v)}
            data-testid="wb-mobile-bar-toggle"
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md border transition-colors ${mobileBarOpen ? "bg-[#0A0C10] text-white border-transparent" : "border-neutral-300 bg-white text-neutral-700"}`}
          >
            <Settings2 size={13} /> Controls
            <ChevronDown size={12} className={`transition-transform ${mobileBarOpen ? "rotate-180" : ""}`} />
          </button>
          <div className="flex-1 min-w-0 truncate text-sm font-display font-bold">{title || "Board"}</div>
          <button onClick={saveBoard} data-testid="wb-save-mobile" className="text-xs px-3 py-1.5 bg-[#0A0C10] text-white rounded-md inline-flex items-center gap-1.5 font-semibold shrink-0">
            <Save size={12} /> Save
          </button>
          {embedded && (
            <button
              onClick={() => { if (window.confirm("Close the Interactive Board and go back to the video call?")) onClose?.(); }}
              data-testid="wb-embedded-close-mobile"
              className="text-xs px-2.5 py-1.5 border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 rounded-md inline-flex items-center gap-1 font-semibold shrink-0"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <div className={`${mobileBarOpen ? "flex flex-wrap items-center gap-1.5 w-full" : "hidden"} lg:contents`}>
        <div className="hidden sm:inline-flex text-[11px] font-mono uppercase tracking-[0.28em] items-center gap-1.5 mr-1" style={{ color: tk.subtext }}>
          <PenTool size={12} />
          {collab ? <span className="text-[#6366F1] font-bold">interactive board · collab</span> : <span>interactive board</span>}
        </div>

        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="wb-title"
          className="min-w-[100px] max-w-[140px] sm:min-w-[160px] sm:max-w-[280px] bg-transparent border-b border-neutral-200 focus:border-[#0A0C10] font-display font-bold text-sm sm:text-base px-1 py-1 outline-none"
        />

        {/* Collab toggle — colourful even in dark mode. Students can't disable
            it; label reflects the current state. */}
        <button
          onClick={onCollabToggle}
          data-testid="wb-collab-toggle"
          className="ml-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-semibold transition-all"
          style={{
            background: collabOn
              ? "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)"
              : (isDark ? "rgba(99,102,241,0.14)" : "#FFFFFF"),
            color: collabOn ? "#FFFFFF" : (isDark ? "#A5B4FC" : "#6366F1"),
            borderColor: collabOn ? "transparent" : (isDark ? "rgba(165,180,252,0.45)" : "#C7D2FE"),
            boxShadow: collabOn ? "0 0 14px rgba(99,102,241,0.55)" : "none",
          }}
        >
          <Users size={13} />
          <span>
            {collabOn
              ? (collabPermission?.allowed_names?.length ? `Remote Access · ${collabPermission.allowed_names.length} student${collabPermission.allowed_names.length === 1 ? "" : "s"}` : "Remote Access · ON")
              : "Remote Access · OFF"}
          </span>
        </button>

        {/* Student affordance — "you can draw" vs view-only, so learners know
            whether the teacher has granted them board access. */}
        {embedded && !isTeacher && !readOnly && (
          <span
            data-testid="wb-student-canwrite"
            className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-600 inline-flex items-center gap-1"
            title="You can draw on the board — the teacher sees your strokes live."
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> You can draw
          </span>
        )}
        {embedded && !isTeacher && readOnly && (
          <span
            data-testid="wb-student-viewonly"
            className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-600 inline-flex items-center gap-1"
            title="The teacher hasn't given you draw access — you're watching."
          >
            👁 View only
          </span>
        )}

        {/* View controls */}
        <div
          className="flex items-center gap-1 ml-2 border rounded-md p-0.5"
          style={{
            borderColor: isDark ? "rgba(255,255,255,0.18)" : "#E5E5E5",
            background: isDark ? "rgba(255,255,255,0.04)" : "transparent",
          }}
        >
          <IconBtn isDark={isDark} onClick={zoomOut} title="Zoom out" testid="wb-zoom-out"><ZoomOut size={13} /></IconBtn>
          <span className="text-[10px] font-mono w-11 text-center" style={{ color: isDark ? "#E5E7EB" : "#3F3F46" }}>{Math.round(view.zoom * 100)}%</span>
          <IconBtn isDark={isDark} onClick={zoomIn} title="Zoom in" testid="wb-zoom-in"><ZoomIn size={13} /></IconBtn>
          <IconBtn isDark={isDark} onClick={resetView} title="Reset view" testid="wb-reset-view"><Maximize2 size={13} /></IconBtn>
          <IconBtn isDark={isDark} onClick={() => setGridOn((v) => !v)} title="Toggle grid" testid="wb-grid-toggle" active={gridOn}><Grid3x3 size={13} /></IconBtn>
          <IconBtn isDark={isDark} onClick={() => setShowCalc((v) => !v)} title="Scientific calculator" testid="wb-calc-toggle" active={showCalc}><Calculator size={13} /></IconBtn>
        </div>

        {/* Assets button — opens the LEFT curriculum panel (Subject → Grade →
            Topic practice). Renamed from "Skills"; the old right-side Assets
            drawer has been retired so a single Assets entry point covers
            everything. */}
        <button
          onClick={() => setShowSkillsPanel((v) => !v)}
          data-testid="wb-assets-toggle"
          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border font-semibold transition-colors ${
            showSkillsPanel
              ? "bg-[#7C3AED] text-white border-[#7C3AED]"
              : isDark
                ? "bg-[#141821] text-[#A78BFA] border-[#7C3AED]/40 hover:bg-[#7C3AED]/15"
                : "bg-[#F5F3FF] text-[#7C3AED] border-[#DDD6FE] hover:bg-[#EDE9FE]"
          }`}
          title="Assets — Subject × Grade × Topic practice"
        >
          <Sparkles size={13} /> Assets
        </button>

        {/* AI Snapshot — teacher-only. In a live class it asks the STUDENTS'
            cameras to capture; standalone it uses the teacher's own camera. */}
        {isTeacher && (
          <button
            onClick={async () => {
              if (embedded && linkedClassId) {
                try {
                  await api.post(`/live/${linkedClassId}/snapshot-request`);
                  toast.success("Snapshot request sent — students' cameras will capture and appear here");
                } catch { /* fall through to gallery anyway */ }
              }
              setSnapshotOpen(true);
            }}
            data-testid="wb-ai-snapshot-toggle"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border font-semibold transition-colors bg-gradient-to-r from-[#0EA5E9] to-[#22D3EE] text-[#062A3A] border-transparent hover:brightness-110"
            title={embedded ? "Ask students to show their paper work — AI transcribes and checks it" : "Show your paper work to the camera — AI transcribes and checks it"}
          >
            <Camera size={13} /> AI Snapshot
          </button>
        )}

        {/* AI Image generator — modal, prompt-only, raster output */}
        <button
          onClick={() => setShowAIModal(true)}
          data-testid="wb-ai-image-toggle"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border font-semibold transition-colors bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white border-transparent hover:brightness-110"
          title="Generate a high-quality AI image from a text prompt"
        >
          <Wand2 size={13} /> AI Image
        </button>

        {/* Insert dropdown — image / PDF / DOCX / URL straight onto the board */}
        <div className="relative" data-testid="wb-insert-wrap">
          <button
            onClick={() => setInsertOpen((v) => !v)}
            data-testid="wb-insert-toggle"
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border font-semibold transition-colors ${
              insertOpen
                ? "bg-[#0EA5E9] text-white border-[#0EA5E9]"
                : isDark
                  ? "bg-[#141821] text-[#38BDF8] border-[#0EA5E9]/40 hover:bg-[#0EA5E9]/15"
                  : "bg-[#F0F9FF] text-[#0369A1] border-[#BAE6FD] hover:bg-[#E0F2FE]"
            }`}
            title="Insert an image, PDF, document or URL onto the board"
          >
            <Plus size={13} /> Insert <ChevronDown size={11} />
          </button>
          {insertOpen && (
            <div
              className="absolute right-0 mt-1 z-40 min-w-[180px] rounded-md shadow-lg border overflow-hidden"
              style={{ background: tk.surface, borderColor: tk.border }}
              data-testid="wb-insert-menu"
              onMouseLeave={() => setInsertOpen(false)}
            >
              {[
                { key: "image", label: "Image", icon: ImagePlus, tint: "#F59E0B", onClick: () => setGalleryOpen(true) },
                { key: "pdf",   label: "PDF",   icon: FileTextIcon, tint: "#EC4899", accept: "application/pdf", onClick: () => pdfFileRef.current?.click() },
                { key: "docx",  label: "DOCX",  icon: FileTextIcon, tint: "#0EA5E9", accept: ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document", onClick: () => docFileRef.current?.click() },
                { key: "url",   label: "URL",   icon: ChevronDown, tint: "#10B981", onClick: () => setInsertUrlOpen(true) },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => { setInsertOpen(false); item.onClick?.(); }}
                  data-testid={`wb-insert-${item.key}`}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:opacity-90 transition-opacity"
                  style={{ background: "transparent", color: tk.text }}
                >
                  <span className="w-6 h-6 rounded flex items-center justify-center text-white shrink-0" style={{ background: item.tint }}>
                    <item.icon size={12} />
                  </span>
                  <span className="font-semibold">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Theme toggle — persists in localStorage. Hidden when the theme is
            forced (students follow the teacher's board theme). */}
        {!forcedTheme && (
        <button
          onClick={() => setTheme((t) => { const nx = t === "dark" ? "light" : "dark"; onThemeChange?.(nx); return nx; })}
          data-testid="wb-theme-toggle"
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border font-mono uppercase tracking-wider transition-colors"
          style={{
            background: isDark ? "#141821" : "white",
            color: isDark ? "#FFD600" : "#404040",
            borderColor: isDark ? "rgba(255,214,0,0.4)" : "#D4D4D4",
          }}
        >
          {isDark ? "☀ Light" : "☾ Dark"}
        </button>
        )}

        <div
          className="flex items-center gap-1 border rounded-md p-0.5"
          style={{
            borderColor: isDark ? "rgba(255,255,255,0.18)" : "#E5E5E5",
            background: isDark ? "rgba(255,255,255,0.04)" : "transparent",
          }}
        >
          <IconBtn isDark={isDark} onClick={undo} title="Undo (Ctrl+Z)" testid="wb-undo"><Undo2 size={13} /></IconBtn>
          <IconBtn isDark={isDark} onClick={redo} title="Redo (Ctrl+Shift+Z)" testid="wb-redo"><Redo2 size={13} /></IconBtn>
        </div>

        <div className="flex-1" />

        {/* Save / new — Boards, PNG and Subject picker moved into the
            Assets drawer to keep the toolbar tidy. */}
        <button onClick={() => setSavedOpen((v) => !v)} data-testid="wb-boards" className="text-xs px-3 py-1.5 border border-neutral-300 hover:border-[#0A0C10] rounded-md inline-flex items-center gap-1.5 text-neutral-700 bg-white">
          <FolderOpen size={12} /> Boards
        </button>
        <button onClick={newBoard} data-testid="wb-new" className="text-xs px-3 py-1.5 border border-neutral-300 hover:border-[#0A0C10] rounded-md inline-flex items-center gap-1.5 text-neutral-700 bg-white">
          <Plus size={12} /> New
        </button>
        <button onClick={saveBoard} data-testid="wb-save" className="text-xs px-3 py-1.5 bg-[#0A0C10] text-white rounded-md inline-flex items-center gap-1.5 font-semibold">
          <Save size={12} /> Save
        </button>
        {embedded && (
          <button
            onClick={() => {
              if (window.confirm("Close the Interactive Board? Any live strokes remain in the class, but the board panel will disappear.")) {
                onClose?.();
              }
            }}
            data-testid="wb-embedded-close"
            className="text-xs px-3 py-1.5 border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 rounded-md inline-flex items-center gap-1.5 font-semibold"
            title="Close the board and go back to the video-call view"
          >
            <X size={14} /> Close board
          </button>
        )}
        </div>
      </div>

      {/* ─── Tool palette (left) + Canvas ─── */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Left toolbar — futuristic colourful rail. Hidden on mobile and for
            view-only students; a bottom sheet with the same tools is rendered
            further down. */}
        {!readOnly && (
        <aside
          className="hidden md:flex w-14 shrink-0 flex-col items-center gap-1 py-2 border-r"
          style={{
            background: isDark
              ? "linear-gradient(180deg, #141821 0%, #0F1218 100%)"
              : tk.railBg,
            color: tk.railText,
            borderColor: tk.border,
            boxShadow: isDark ? "inset -1px 0 0 rgba(255,255,255,0.03)" : "none",
          }}
          data-testid="wb-toolpanel"
        >
          {TOOLS.map((t) => {
            const active = tool === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTool(t.id)}
                title={t.title}
                data-testid={`wb-tool-${t.id}`}
                className="w-10 h-10 rounded-md flex items-center justify-center transition-all duration-150 relative group"
                style={{
                  background: active
                    ? (isDark ? `${t.color}22` : `${t.color}18`)
                    : "transparent",
                  color: t.color,
                  boxShadow: active
                    ? `inset 0 0 0 1.5px ${t.color}, 0 0 12px -2px ${t.color}80`
                    : "none",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = isDark ? `${t.color}18` : `${t.color}14`;
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                <t.icon size={16} strokeWidth={active ? 2.4 : 2} />
                {active && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r"
                    style={{ background: t.color, boxShadow: `0 0 8px ${t.color}` }}
                  />
                )}
              </button>
            );
          })}
          <div className="w-8 h-px opacity-20 my-1" style={{ background: tk.text }} />
          <button onClick={() => setImgPalette(true)} title="Search images (Openverse)" data-testid="wb-tool-search-image"
            className="w-10 h-10 rounded-md flex items-center justify-center transition-colors"
            style={{ color: "#22D3EE" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "#22D3EE18" : "#22D3EE14"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
            <Search size={16} />
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFileImport} className="hidden" data-testid="wb-file-input" />
          <input ref={pdfFileRef} type="file" accept="application/pdf,.pdf" onChange={onPdfImport} className="hidden" data-testid="wb-pdf-input" />
          <input
            ref={docFileRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const fd = new FormData();
                fd.append("file", f);
                toast.loading("Extracting docx…", { id: "docx-import" });
                const { data } = await api.post("/ai/slides/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const text = (data?.text || "").slice(0, 4000);
                if (!text.trim()) { toast.dismiss("docx-import"); toast.error("No selectable text found"); return; }
                addElement({ id: rid(), type: "sticky", x: 340, y: 260, width: 320, height: 220, text, color: "#FEF3C7", fontSize: 12 });
                toast.dismiss("docx-import"); toast.success("Docx imported as sticky note");
              } catch (err) {
                toast.dismiss("docx-import");
                toast.error(err.response?.data?.detail || "Docx import failed");
              } finally {
                if (docFileRef.current) docFileRef.current.value = "";
              }
            }}
            className="hidden"
            data-testid="wb-docx-input"
          />
          <div className="w-8 h-px opacity-20 my-1" style={{ background: tk.text }} />
          {/* Scientific calculator — always available, prominent shortcut */}
          <button
            onClick={() => setShowCalc((v) => !v)}
            data-testid="wb-tool-calc"
            title="Scientific calculator"
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-150 hover:scale-110"
            style={{
              color: showCalc ? "#0A0C10" : "#10B981",
              background: showCalc ? "linear-gradient(135deg,#34D399,#22D3EE)" : "linear-gradient(135deg,rgba(16,185,129,0.14),rgba(34,211,238,0.14))",
              boxShadow: showCalc ? "0 0 16px -2px rgba(16,185,129,0.7)" : "inset 0 0 0 1.5px rgba(16,185,129,0.45)",
            }}
          >
            <Calculator size={17} strokeWidth={2.5} />
          </button>
          <div className="w-8 h-px opacity-20 my-1" style={{ background: tk.text }} />
          {/* Geometry tools — only visible when the linked subject is Maths.
              Tinted indigo/purple so students recognise them as maths-only. */}
          {(() => {
            const subjName = (selectedSubject?.name || "").toLowerCase();
            const isMaths = /math|geometry|algebra|calculus|trigonom|statistic/.test(subjName);
            if (!isMaths) return null;
            const geoBtn = (activeCol, col, iconEl, onClick, testid, title) => (
              <button
                onClick={onClick}
                data-testid={testid}
                title={title}
                className="w-10 h-10 rounded-md flex items-center justify-center transition-all duration-150 relative"
                style={{
                  color: col,
                  background: activeCol ? `${col}22` : "transparent",
                  boxShadow: activeCol ? `inset 0 0 0 1.5px ${col}, 0 0 10px -2px ${col}80` : "none",
                }}
                onMouseEnter={(e) => { if (!activeCol) e.currentTarget.style.background = isDark ? `${col}18` : `${col}14`; }}
                onMouseLeave={(e) => { if (!activeCol) e.currentTarget.style.background = "transparent"; }}
              >{iconEl}</button>
            );
            return (
              <>
                {geoBtn(!!ruler, "#6366F1", <Ruler size={16} strokeWidth={ruler ? 2.4 : 2} />, spawnGeoRuler, "wb-tool-ruler", "Ruler")}
                {geoBtn(!!protractor, "#8B5CF6", <Triangle size={16} strokeWidth={protractor ? 2.4 : 2} />, spawnGeoProtractor, "wb-tool-protractor", "Protractor")}
                {geoBtn(!!compass, "#EC4899", <Compass size={16} strokeWidth={compass ? 2.4 : 2} />, spawnGeoCompass, "wb-tool-compass", "Compass")}
                <div className="w-8 h-px opacity-20 my-1" style={{ background: tk.text }} />
              </>
            );
          })()}
          <button onClick={deleteSelected} title="Delete selection (Del)" data-testid="wb-delete-sel" disabled={!selectedId}
            className="w-10 h-10 rounded-md flex items-center justify-center transition-colors disabled:opacity-25"
            style={{ color: "#F87171" }}
            onMouseEnter={(e) => { if (selectedId) e.currentTarget.style.background = isDark ? "#F8717118" : "#F8717114"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
            <Trash2 size={15} />
          </button>
          <button onClick={clearAll} title="Clear whole board" data-testid="wb-clear-all"
            className="w-10 h-10 rounded-md flex items-center justify-center transition-colors"
            style={{ color: "#94A3B8" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "#94A3B818" : "#94A3B814"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
            <Eraser size={15} />
          </button>
        </aside>
        )}

        {/* ── Mobile toolbelt (visible below md) ────────────────────────────
            Compact FAB that opens a bottom sheet with the full tool palette,
            colour picker, stroke widths, and quick actions. Mirrors the
            desktop left rail so nothing is lost on phones/tablets. */}
        {!readOnly && (
        <button
          onClick={() => setMobileToolSheetOpen(true)}
          data-testid="wb-mobile-tools-fab"
          className={`md:hidden fixed left-4 ${embedded ? "bottom-24" : "bottom-4"} z-[65] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center border-2`}
          style={{
            background: isDark ? "#141821" : "#FFFFFF",
            borderColor: activeToolMeta.color,
            color: activeToolMeta.color,
            boxShadow: `0 0 24px -4px ${activeToolMeta.color}80, 0 8px 20px rgba(0,0,0,0.35)`,
          }}
          title={`Current tool · ${activeToolMeta.title}`}
        >
          <activeToolMeta.icon size={22} strokeWidth={2.4} />
        </button>
        )}

        {/* Portal to <body> so the sheet escapes the board panel's stacking
            context and sits above the live-class PIP + floating call bar. */}
        {mobileToolSheetOpen && createPortal(
          <div
            className="md:hidden fixed inset-0 z-[97] flex items-end"
            data-testid="wb-mobile-sheet"
            onClick={() => setMobileToolSheetOpen(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-h-[80vh] rounded-t-2xl shadow-2xl overflow-y-auto"
              style={{
                background: isDark ? "linear-gradient(180deg, #141821 0%, #0F1218 100%)" : "#FFFFFF",
                color: tk.text,
                borderTop: `1px solid ${tk.border}`,
              }}
            >
              {/* Drag handle */}
              <div className="w-full flex flex-col items-center pt-2 pb-1">
                <div className="w-10 h-1.5 rounded-full" style={{ background: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }} />
              </div>
              <div className="px-4 pb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: tk.subtext }}>tools</div>
                  <button
                    onClick={() => setMobileToolSheetOpen(false)}
                    data-testid="wb-mobile-sheet-close"
                    className="p-1.5 rounded hover:bg-white/10"
                    style={{ color: tk.text }}
                  ><X size={16} /></button>
                </div>

                {/* Tool grid */}
                <div className="grid grid-cols-6 gap-2 mb-4">
                  {TOOLS.map((t) => {
                    const active = tool === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => { setTool(t.id); setMobileToolSheetOpen(false); }}
                        data-testid={`wb-mtool-${t.id}`}
                        className="aspect-square rounded-lg flex items-center justify-center transition-all"
                        style={{
                          background: active ? `${t.color}22` : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
                          color: t.color,
                          boxShadow: active ? `inset 0 0 0 1.5px ${t.color}, 0 0 12px -2px ${t.color}80` : "none",
                        }}
                        title={t.title}
                      >
                        <t.icon size={20} strokeWidth={active ? 2.4 : 2} />
                      </button>
                    );
                  })}
                </div>

                {/* Colours */}
                <div className="mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] mb-2" style={{ color: tk.subtext }}>colour</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setColor(c)}
                        data-testid={`wb-mcolor-${c.replace('#','')}`}
                        className={`w-8 h-8 rounded-full transition-transform ${color === c ? "scale-110 ring-2 ring-offset-2" : ""}`}
                        style={{
                          background: c,
                          boxShadow: color === c ? `0 0 12px ${c}` : "none",
                          ["--tw-ring-color"]: c,
                          ["--tw-ring-offset-color"]: isDark ? "#0F1218" : "#FFFFFF",
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Stroke widths */}
                <div className="mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] mb-2" style={{ color: tk.subtext }}>stroke</div>
                  <div className="flex items-center gap-2">
                    {STROKE_WIDTHS.map((w) => (
                      <button
                        key={w}
                        onClick={() => setStrokeWidth(w)}
                        data-testid={`wb-mw-${w}`}
                        className="w-10 h-10 rounded flex items-center justify-center transition-colors"
                        style={{
                          background: strokeWidth === w ? "#0A0C10" : "transparent",
                          border: `1px solid ${strokeWidth === w ? "transparent" : tk.border}`,
                        }}
                      >
                        <span className="rounded-full block" style={{ width: Math.min(w + 2, 18), height: Math.min(w + 2, 18), background: strokeWidth === w ? "#FFD600" : color }} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick actions */}
                <div className="grid grid-cols-4 gap-2 pt-3 border-t" style={{ borderColor: tk.border }}>
                  <button
                    onClick={() => { undo(); setMobileToolSheetOpen(false); }}
                    data-testid="wb-mundo"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border"
                    style={{ borderColor: tk.border, color: tk.text }}
                  ><Undo2 size={13} /> Undo</button>
                  <button
                    onClick={() => { redo(); setMobileToolSheetOpen(false); }}
                    data-testid="wb-mredo"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border"
                    style={{ borderColor: tk.border, color: tk.text }}
                  ><Redo2 size={13} /> Redo</button>
                  <button
                    onClick={() => { deleteSelected(); setMobileToolSheetOpen(false); }}
                    disabled={!selectedId}
                    data-testid="wb-mdelete"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border disabled:opacity-40"
                    style={{ borderColor: "#F87171", color: "#F87171" }}
                  ><Trash2 size={13} /> Del</button>
                  <button
                    onClick={() => { clearAll(); setMobileToolSheetOpen(false); }}
                    data-testid="wb-mclear"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border"
                    style={{ borderColor: tk.border, color: tk.subtext }}
                  ><Eraser size={13} /> Clear</button>
                </div>

                {/* View controls */}
                <div className="grid grid-cols-4 gap-2 mt-2">
                  <button
                    onClick={zoomOut}
                    data-testid="wb-mzoom-out"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border"
                    style={{ borderColor: tk.border, color: tk.text }}
                  ><ZoomOut size={13} /></button>
                  <button
                    onClick={zoomIn}
                    data-testid="wb-mzoom-in"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border"
                    style={{ borderColor: tk.border, color: tk.text }}
                  ><ZoomIn size={13} /></button>
                  <button
                    onClick={resetView}
                    data-testid="wb-mreset-view"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border"
                    style={{ borderColor: tk.border, color: tk.text }}
                  ><Maximize2 size={13} /></button>
                  <button
                    onClick={() => setGridOn((v) => !v)}
                    data-testid="wb-mgrid"
                    className="py-2.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 border"
                    style={{ borderColor: gridOn ? "#FFD600" : tk.border, color: gridOn ? "#FFD600" : tk.text }}
                  ><Grid3x3 size={13} /></button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Style strip — sits to the right of the left rail on desktop.
            On mobile it hides here and is shown inside the bottom sheet.
            Uses a brighter surface + soft glow in dark mode so the colour
            palette and stroke previews stand out (fixes "blended with
            background" feedback). */}
        <div
          className="hidden md:flex absolute left-16 top-3 z-20 rounded-lg shadow-2xl px-2.5 py-1.5 items-center gap-2 border backdrop-blur-md"
          data-testid="wb-style-strip"
          style={{
            background: isDark ? "rgba(30,36,48,0.92)" : "#FFFFFF",
            borderColor: isDark ? "rgba(255,255,255,0.15)" : tk.border,
            boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)" : undefined,
          }}
        >
          {/* Colors */}
          <div className="flex items-center gap-1">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} title={c} data-testid={`wb-color-${c.replace('#','')}`}
                className={`w-6 h-6 rounded-full transition-transform ${color === c ? "scale-125 ring-2 ring-offset-1 ring-white" : "hover:scale-110"}`}
                style={{
                  background: c,
                  border: `2px solid ${isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.15)"}`,
                  boxShadow: color === c ? `0 0 14px ${c}` : "none",
                }} />
            ))}
          </div>
          <div className="w-px h-6" style={{ background: isDark ? "rgba(255,255,255,0.15)" : tk.border }} />
          {/* Stroke widths — visible pill on both themes; selected shows a
              coloured filled dot on a bright yellow background. */}
          <div className="flex items-center gap-1" title="Stroke width">
            {STROKE_WIDTHS.map((w) => (
              <button key={w} onClick={() => setStrokeWidth(w)} data-testid={`wb-w-${w}`} title={`${w}px stroke`}
                className="w-8 h-8 rounded-md flex items-center justify-center transition-all"
                style={{
                  background: strokeWidth === w
                    ? (isDark ? "#FFD600" : "#0A0C10")
                    : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"),
                  border: `1px solid ${strokeWidth === w ? "#FFD600" : (isDark ? "rgba(255,255,255,0.15)" : tk.border)}`,
                  boxShadow: strokeWidth === w ? "0 0 8px rgba(255,214,0,0.55)" : "none",
                }}>
                <span
                  className="rounded-full block"
                  style={{
                    width: Math.min(w + 3, 16),
                    height: Math.min(w + 3, 16),
                    background: strokeWidth === w ? "#0A0C10" : color,
                    boxShadow: strokeWidth === w ? "none" : `0 0 6px ${color}80`,
                  }}
                />
              </button>
            ))}
          </div>
          {tool === "sticky" && (
            <>
              <div className="w-px h-5 bg-black/10" />
              <div className="flex items-center gap-1">
                {STICKY_TINTS.map((t) => (
                  <button key={t} onClick={() => setStickyTint(t)} data-testid={`wb-sticky-${t.replace('#','')}`}
                    className={`w-5 h-5 rounded border ${stickyTint === t ? "border-[#0A0C10] scale-110" : "border-black/20"}`}
                    style={{ background: t }} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Peer badge */}
        {collab && peerCursors.length > 0 && (
          <div className="absolute right-3 top-3 z-20 bg-white border border-black/10 rounded-md shadow-sm px-2.5 py-1 inline-flex items-center gap-1.5" data-testid="wb-peer-badge">
            <Users size={11} className="text-[#6366F1]" />
            <div className="flex -space-x-1.5">
              {peerCursors.slice(0, 4).map((p) => (
                <div key={p.user_id} title={p.user_name} className="w-5 h-5 rounded-full bg-[#6366F1] text-white text-[9px] font-bold flex items-center justify-center border border-white">
                  {(p.user_name || "?").slice(0, 1).toUpperCase()}
                </div>
              ))}
            </div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{peerCursors.length} live</span>
          </div>
        )}

        {/* Canvas */}
        <WhiteboardCanvas
          view={view} setView={setView}
          elements={elements} setElements={setElements}
          strokes={strokes}
          selectedId={selectedId} setSelectedId={setSelectedId}
          editing={editing} setEditing={setEditing}
          tool={readOnly ? "pan" : tool} color={color} strokeWidth={strokeWidth}
          onStrokeEnd={readOnly ? () => {} : onStrokeEnd}
          onEraseStrokes={readOnly ? undefined : eraseStrokes}
          onElementCreate={readOnly ? () => {} : onElementCreate}
          onElementCommit={readOnly ? () => {} : onElementCommit}
          onElementDelete={readOnly ? () => {} : pushDeleteElement}
          onCursorMove={collab ? onCursorMove : undefined}
          peerCursors={collab ? peerCursors : []}
          gridOn={gridOn}
          theme={effTheme}
          onContainerRef={(ref) => { canvasContainerRef.current = ref.current; }}
          overlaySvg={
            <>
              {ruler && (
                <GeometryRuler state={ruler} setState={setRuler} containerRef={canvasContainerRef} viewRef={viewRef} onClose={() => setRuler(null)} />
              )}
              {protractor && (
                <GeometryProtractor state={protractor} setState={setProtractor} containerRef={canvasContainerRef} viewRef={viewRef} onClose={() => setProtractor(null)} />
              )}
              {compass && (
                <GeometryCompass state={compass} setState={setCompass} containerRef={canvasContainerRef} viewRef={viewRef} onClose={() => setCompass(null)} onDrawArc={onCompassDrawArc} />
              )}
            </>
          }
        />

        {/* PDF import progress */}
        {pdfBusy.on && (
          <div className="absolute left-1/2 -translate-x-1/2 top-3 z-30 bg-white border border-black/10 rounded-md shadow-md px-4 py-2 inline-flex items-center gap-2" data-testid="wb-pdf-progress">
            <Loader2 size={14} className="animate-spin text-[#6366F1]" />
            <span className="text-xs font-mono">Rendering PDF · page {pdfBusy.page || 0}{pdfBusy.total ? ` / ${pdfBusy.total}` : "…"}</span>
          </div>
        )}

        {/* PDF page-jump nav — thumbnails on the right edge */}
        {pdfPages.length > 0 && (
          <div className="absolute right-2 top-16 bottom-4 z-30 w-[92px] bg-white/90 backdrop-blur border border-black/10 rounded-md shadow-md p-1 overflow-y-auto" data-testid="wb-pdf-nav">
            <div className="text-[9px] font-mono uppercase tracking-wider text-neutral-500 text-center py-1 border-b border-black/10 mb-1 sticky top-0 bg-white/90">
              {pdfPages.length} pages
              <button onClick={() => setPdfPages([])} data-testid="wb-pdf-nav-close" className="ml-1 text-red-400 hover:text-red-600" title="Hide">×</button>
            </div>
            {pdfPages.map((p, i) => (
              <button
                key={p.id}
                data-testid={`wb-pdf-nav-page-${i + 1}`}
                onClick={() => {
                  // Center the view on page p
                  setView((v) => ({ ...v, panX: p.x - 40, panY: p.y - 40 }));
                }}
                className="w-full mb-1 border border-neutral-200 rounded hover:border-[#6366F1] hover:ring-1 hover:ring-[#6366F1] transition-all group overflow-hidden bg-white"
                title={p.title}
              >
                <img src={p.thumb} alt={p.title} className="w-full h-16 object-cover object-top" />
                <div className="text-[9px] font-mono text-center py-0.5 bg-neutral-50 group-hover:bg-[#EEF2FF] text-[#0A0C10]">{i + 1}</div>
              </button>
            ))}
          </div>
        )}

        {/* Subject footer — dynamic sticky footer with subject-specific tools */}
        <SubjectFooter subject={selectedSubject} onAddProp={insertProp} />

        {/* Insert URL modal */}
        {insertUrlOpen && (
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            data-testid="wb-insert-url-modal"
            onClick={() => setInsertUrlOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-[480px] max-w-[92vw] bg-[#0F1218] border border-[#10B981]/40 rounded-xl shadow-2xl overflow-hidden text-white"
            >
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#10B981]">/ insert URL</div>
                  <div className="font-display font-bold text-lg tracking-tight mt-0.5">Add a link card</div>
                </div>
                <button onClick={() => setInsertUrlOpen(false)} className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-3">
                <input
                  autoFocus
                  value={insertUrlValue}
                  onChange={(e) => setInsertUrlValue(e.target.value)}
                  placeholder="https://…"
                  data-testid="wb-insert-url-input"
                  className="w-full text-sm bg-[#141821] border border-white/10 rounded-md p-3 outline-none focus:border-[#10B981] text-white placeholder:text-white/30"
                />
                <button
                  onClick={() => {
                    const url = insertUrlValue.trim();
                    if (!url) return;
                    addElement({
                      id: rid(), type: "sticky",
                      x: 340, y: 260, width: 320, height: 100,
                      text: `🔗 ${url}`, color: "#DCFCE7", fontSize: 13,
                    });
                    toast.success("Link card added");
                    setInsertUrlValue(""); setInsertUrlOpen(false);
                  }}
                  data-testid="wb-insert-url-add"
                  disabled={!insertUrlValue.trim()}
                  className="w-full py-3 rounded-md font-bold text-sm uppercase tracking-wide text-[#0A0C10] bg-[#10B981] hover:brightness-110 disabled:opacity-50"
                >
                  Add to board
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI image modal — replaces the old floating footer panel.
            Opens on the "AI Image" toolbar button. Prompt-only flow with
            a futuristic animated gradient during generation. */}
        {showAIModal && (
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            data-testid="wb-ai-image-modal"
            onClick={() => !aiLoading && setShowAIModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative w-[520px] max-w-[92vw] bg-[#0F1218] border border-[#7C3AED]/40 rounded-xl shadow-2xl overflow-hidden text-white"
            >
              {/* Header */}
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#EC4899]">/ ai image generator</div>
                  <div className="font-display font-bold text-lg tracking-tight mt-0.5">Prompt → picture</div>
                </div>
                <button
                  onClick={() => !aiLoading && setShowAIModal(false)}
                  data-testid="wb-ai-modal-close"
                  className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10 disabled:opacity-30"
                  disabled={aiLoading}
                ><X size={16} /></button>
              </div>
              {/* Body */}
              <div className="p-5 space-y-3 relative">
                <textarea
                  rows={4}
                  value={aiPrompt}
                  onChange={(e) => setAIPrompt(e.target.value)}
                  placeholder="Describe the image you want. e.g. 'A labelled diagram of the human heart with chambers and arteries'"
                  data-testid="wb-ai-modal-prompt"
                  disabled={aiLoading}
                  className="w-full text-sm bg-[#141821] border border-white/10 rounded-md p-3 outline-none focus:border-[#EC4899] text-white placeholder:text-white/30 resize-none"
                />
                <button
                  onClick={async () => {
                    setShowAIModal(false);
                    setGenFx("Synthesizing image");
                    try { await generateAI(); } finally { setGenFx(null); }
                  }}
                  disabled={aiLoading || !aiPrompt.trim()}
                  data-testid="wb-ai-modal-generate"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md font-bold text-sm tracking-wide uppercase text-[#0A0C10] bg-gradient-to-r from-[#FFD600] to-[#EC4899] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {aiLoading ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Wand2 size={14} /> Generate image (4 credits)</>}
                </button>
                {/* Futuristic loading overlay */}
                {aiLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#0F1218]/70 backdrop-blur-[2px]">
                    <div className="text-center">
                      <div className="relative w-24 h-24 mx-auto">
                        <div className="absolute inset-0 rounded-full border-2 border-[#EC4899]/40 animate-ping" />
                        <div className="absolute inset-2 rounded-full border-2 border-[#7C3AED]/60 animate-ping" style={{ animationDelay: "0.3s" }} />
                        <div className="absolute inset-4 rounded-full border-2 border-[#FFD600]/80 animate-ping" style={{ animationDelay: "0.6s" }} />
                        <div className="absolute inset-8 rounded-full bg-gradient-to-r from-[#FFD600] via-[#EC4899] to-[#7C3AED] animate-pulse" />
                      </div>
                      <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.28em] text-white/80">Rendering visual…</div>
                      <div className="mt-1 text-[10px] font-mono text-white/40">this can take 8–15 seconds</div>
                    </div>
                  </div>
                )}
              </div>
              {/* Animated grid backdrop */}
              <div
                className="absolute inset-0 pointer-events-none opacity-10"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(124,58,237,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(236,72,153,0.4) 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                }}
              />
            </div>
          </div>
        )}

        {/* Saved boards popover */}
        {savedOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="absolute right-3 top-14 z-30 w-[280px] bg-white border border-black/10 rounded-lg shadow-xl overflow-hidden"
            data-testid="wb-saved-panel"
          >
            <div className="px-3 py-2 border-b border-black/10 flex items-center justify-between">
              <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">Saved boards</span>
              <button onClick={() => setSavedOpen(false)}><X size={12} /></button>
            </div>
            <div className="max-h-[300px] overflow-y-auto p-1">
              {scenes.length === 0 && <div className="p-3 text-xs text-neutral-400">Save your first board to see it here.</div>}
              {scenes.map((s) => (
                <div key={s.id} onClick={() => openScene(s.id)}
                  className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer group ${s.id === currentSceneId ? "bg-neutral-100" : "hover:bg-neutral-50"}`}
                  data-testid={`wb-load-${s.id}`}
                >
                  <div className="flex-1 text-sm truncate">{s.title || "Untitled"}</div>
                  <button onClick={(e) => deleteScene(s.id, e)} data-testid={`wb-del-${s.id}`} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      <ImageSearchPalette
        open={imgPalette}
        onClose={() => setImgPalette(false)}
        onPick={addImageFromSearch}
        defaultQ={aiPrompt}
      />

      {/* Floating scientific calculator — draggable, keyboard-friendly */}
      {showCalc && <ScientificCalculator onClose={() => setShowCalc(false)} />}

      {/* Curriculum Skills Panel — left-side column, subject × grade × topic practice */}
      {showSkillsPanel && (
        <div className="fixed left-0 top-[64px] bottom-0 z-[45]" data-testid="wb-skills-panel-root">
          <CurriculumSkillsPanel
            onPractice={dropSkillWidget}
            onVisual={dropTopicVisual}
            onClose={() => setShowSkillsPanel(false)}
          />
        </div>
      )}

      {/* Right-side Assets panel has been retired. The "Assets" toolbar
          button now opens the unified LEFT curriculum panel above. */}

      {/* AI Snapshot — camera capture + vision review. Also opens in review
          mode when the teacher clicks a student's incoming snapshot toast. */}
      {(snapshotOpen || snapshotReview) && (
        <SnapshotModal
          open
          classId={linkedClassId}          isTeacher={isTeacher}
          startMode={embedded && isTeacher && linkedClassId ? "gallery" : "camera"}
          review={snapshotReview}
          onClose={() => { setSnapshotOpen(false); setSnapshotReview(null); }}
          onPlaceOnBoard={(url) => {
            pushHistory();
            const w = 420, h = 540;
            const cx = -view.panX + (window.innerWidth / 2) / view.zoom - w / 2;
            const cy = -view.panY + (window.innerHeight / 2) / view.zoom - h / 2;
            addElement(newImage(url, cx, cy, w, h, "AI Snapshot"));
            setSnapshotOpen(false); setSnapshotReview(null);
            toast.success("Snapshot placed on the board — annotate away");
          }}
        />
      )}

      {/* Cloud Gallery — universal image source (device upload or reuse) */}
      <CloudGalleryPicker open={galleryOpen} onClose={() => setGalleryOpen(false)} onSelect={placeGalleryImage} />

      {/* Futuristic AI-generation overlay */}
      {genFx && <NeonGenOverlay label={genFx} />}

      {/* Teacher: live student responses for skill-widget quizzes on the board */}
      {embedded && isTeacher && linkedClassId && (() => {
        const skillWidgets = elements
          .filter((e) => e.type === "prop" && e.propType === "skill-widget" && !e.deleted)
          .map((e) => ({ id: e.id, title: e.state?.title || "Skill quiz", questions: e.state?.questions || [] }));
        return skillWidgets.length ? <SkillResponsesPanel classId={linkedClassId} widgets={skillWidgets} /> : null;
      })()}
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, active, isDark }) {
  const bg = active
    ? "#FFD600"
    : "transparent";
  const color = active
    ? "#0A0C10"
    : isDark ? "#E5E7EB" : "#3F3F46";
  const boxShadow = active ? "0 0 10px rgba(255,214,0,0.5)" : "none";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-1.5 rounded transition-all"
      style={{ background: bg, color, boxShadow }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "#F5F5F5";
          e.currentTarget.style.color = isDark ? "#FFFFFF" : "#0A0C10";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = isDark ? "#E5E7EB" : "#3F3F46";
        }
      }}
    >
      {children}
    </button>
  );
}
